import React, { useEffect, useState } from 'react';
import { isPhoneNumber } from 'class-validator';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalInput } from '../../components/FormField/PortalInput';
import { FormGrid } from '../../components/FormField/FormGrid';
import { RescueAccountView } from '../../lib/accountAdapters';
import { useResellerContactSave } from './useAccountSave';
import styles from './Account.module.scss';

/** Pragmatic email shape check — backend remains authoritative. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidUsPhone(value: string): boolean {
  return isPhoneNumber(value, 'US') || isPhoneNumber(value, 'PR') || isPhoneNumber(value, 'VI');
}

interface EditPrimaryContactModalProps {
  open: boolean;
  onClose: () => void;
  view: RescueAccountView;
}

export default function EditPrimaryContactModal({ open, onClose, view }: EditPrimaryContactModalProps) {
  const { save, saving } = useResellerContactSave();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (open) {
      setFirstName(view.primaryContact?.firstName ?? '');
      setLastName(view.primaryContact?.lastName ?? '');
      setPhone(view.primaryContact?.phone ?? '');
      setEmail(view.primaryContact?.email ?? '');
      setShowErrors(false);
    }
  }, [open, view]);

  const firstNameError = firstName.trim() ? undefined : 'Please enter a first name';
  const phoneError = !phone.trim()
    ? 'Please enter a phone number'
    : !isValidUsPhone(phone.trim())
      ? 'Please enter a valid phone number'
      : undefined;
  const emailError = !email.trim()
    ? 'Please enter an email'
    : !EMAIL_RE.test(email.trim())
      ? 'Please enter a valid email'
      : undefined;

  async function handleSave() {
    if (firstNameError || phoneError || emailError) {
      setShowErrors(true);
      return;
    }
    if (!view.resellerId) {
      return;
    }
    const ok = await save(
      {
        resellerId: view.resellerId,
        contactInfo: {
          firstName: firstName.trim(),
          lastName: lastName.trim() || null,
          primaryPhone: phone.trim(),
          primaryEmail: email.trim().toLowerCase(),
          country: 'US',
        },
      },
      'Primary contact updated',
    );
    if (ok) {
      onClose();
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      <ModalTitle>Edit Primary Contact</ModalTitle>
      <p className={styles.modalDesc}>The person Fi reaches about your account, orders, and program updates.</p>

      <FormGrid>
        <PortalField label="First name" error={firstNameError} showError={showErrors && !!firstNameError}>
          <PortalInput
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            hasError={showErrors && !!firstNameError}
          />
        </PortalField>
        <PortalField label="Last name">
          <PortalInput value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" />
        </PortalField>
      </FormGrid>

      <FormGrid>
        <PortalField label="Primary phone" error={phoneError} showError={showErrors && !!phoneError}>
          <PortalInput
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 555-0142"
            hasError={showErrors && !!phoneError}
          />
        </PortalField>
        <PortalField label="Primary email" error={emailError} showError={showErrors && !!emailError}>
          <PortalInput
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@yourrescue.org"
            hasError={showErrors && !!emailError}
          />
        </PortalField>
      </FormGrid>

      <ModalActions>
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
